import { Box, Container, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import { ComponentProps, CSSProperties, ReactNode } from 'react';
import { useHeaderStyles } from './page-header.styles';
import { Popover } from '../popover/popover.component';
import { Breakpoint } from '@mui/material/styles';

interface BaseInfoProps {
  Icon?: SvgIconComponent;
  text?: string;
  popover?: false;
}

interface InfoWithPopoverProps {
  Icon?: SvgIconComponent;
  text?: string;
  popover: true;
  title: string;
  detail: string;
}

type InfoProps = BaseInfoProps | InfoWithPopoverProps;

// STANDARD PAGE HEADER STYLES - USE THIS TO WRAP ALL PAGE HEADERS
export function PageHeaderWrapper({
  children,
  defaultHeight
}: {
  defaultHeight?: number;
  children: JSX.Element | JSX.Element[];
}) {
  const classes = useHeaderStyles({ defaultHeight });
  return <Container className={classes.pageHeader}>{children}</Container>;
}

// RESPONSIVE WRAPPER AROUND MUI GRID TO CONTROL ALIGNMENT OF CONTENT ACROSS BREAKPOINTS
export function PageHeaderGrid({
  children,
  alignItems,
  leftAlignAt = 'lg',
  ...gridProps
}: {
  children: ReactNode;
  leftAlignAt?: Breakpoint | 'never';
  // Grid v9 dropped alignItems; it lives in sx now, so we take it as our own prop.
  alignItems: CSSProperties['alignItems'];
} & ComponentProps<typeof Grid>) {
  const theme = useTheme();
  const isLeftAlignBreakpoint = useMediaQuery(theme.breakpoints.down(leftAlignAt === 'never' ? 'lg' : leftAlignAt));
  const shouldLeftAlign = leftAlignAt !== 'never' && isLeftAlignBreakpoint;
  const currentAlignment = shouldLeftAlign ? 'flex-start' : alignItems;
  return (
    <Grid
      container
      {...gridProps}
      sx={[
        {
          alignItems: currentAlignment,
          flexDirection: 'column'
        },
        ...(Array.isArray(gridProps.sx) ? gridProps.sx : [gridProps.sx])
      ]}
    >
      {currentAlignment === 'center' ? <Box>{children}</Box> : children}
    </Grid>
  );
}

export function Title({ text }: { text: string }): JSX.Element {
  const classes = useHeaderStyles({});
  return (
    <Box className={classes.title} component="h2">
      {text}
    </Box>
  );
}

export function Subtitle({ text, ...rest }: InfoProps) {
  const classes = useHeaderStyles({});
  return (
    <InfoWrapper {...rest}>
      <p className={classes.subtitle}>{text}</p>
    </InfoWrapper>
  );
}

export function Info({ Icon, text, placeholder }: { Icon?: SvgIconComponent; text?: string; placeholder?: string }) {
  const classes = useHeaderStyles({});
  return !text && !placeholder ? null : (
    <Typography className={classes.info}>
      {Icon && (
        <>
          <Icon />
          &nbsp;
        </>
      )}
      {!text ? placeholder : text}
    </Typography>
  );
}

function InfoWrapper(props: InfoProps & { children: ReactNode }) {
  return props.popover ? (
    <Box style={{ display: 'flex' }}>
      {props.children}
      <Popover title={props.title} text={props.detail} />
    </Box>
  ) : (
    <>{props.children}</>
  );
}
